import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { trySetServiceUnavailableError } from '../../utils/serviceUnavailableMessages';

const VerifyEmail = ({ token, onSuccess, onSwitchToLogin }) => {
  const { verifyEmail } = useAuth();
  const { t } = useLanguage();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');

  useEffect(() => {
    const verify = async () => {
      try {
        await verifyEmail(token);
        setStatus('success');
        // Auto redirect after 3 seconds
        setTimeout(() => {
          onSuccess();
        }, 3000);
      } catch (err) {
        setStatus('error');
        if (trySetServiceUnavailableError(err, setError, t)) return;
        const errorText = err.data?.errorText;
        if (typeof errorText === 'string') {
          setError(errorText);
        } else if (typeof errorText === 'object') {
          const messages = Object.values(errorText).flat();
          setError(messages.join(' '));
        } else {
          setError(t('verifyEmail.failedError'));
        }
      }
    };

    if (token) {
      verify();
    } else {
      setStatus('error');
      setError(t('verifyEmail.invalidLink'));
    }
  }, [token, verifyEmail, onSuccess]);

  if (status === 'verifying') {
    return (
      <Card className="auth-card" style={{ maxWidth: '420px', width: '100%' }}>
        <Card.Body className="p-4 p-md-5 text-center">
          <div className="icon-circle bg-info bg-gradient mx-auto mb-3">
            <i className="bi bi-hourglass-split text-white"></i>
          </div>
          <h2 className="fw-bold">{t('verifyEmail.verifyingTitle')}</h2>
          <p className="text-muted mb-4">{t('verifyEmail.verifyingMessage')}</p>
          <Spinner animation="border" variant="primary" />
        </Card.Body>
      </Card>
    );
  }

  if (status === 'success') {
    return (
      <Card className="auth-card" style={{ maxWidth: '420px', width: '100%' }}>
        <Card.Body className="p-4 p-md-5 text-center">
          <div className="icon-circle bg-success bg-gradient mx-auto mb-3">
            <i className="bi bi-check-lg text-white"></i>
          </div>
          <h2 className="fw-bold">{t('verifyEmail.successTitle')}</h2>
          <p className="text-muted mb-4">{t('verifyEmail.successMessage')}</p>

          <Alert variant="success" className="text-start">
            <div className="d-flex">
              <i className="bi bi-check-circle-fill me-2 mt-1"></i>
              <div>
                {t('verifyEmail.successInfo')}
              </div>
            </div>
          </Alert>

          <Button
            className="btn-gradient w-100 py-2 mt-3"
            size="lg"
            onClick={onSwitchToLogin}
          >
            {t('verifyEmail.continueToSignIn')}
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="auth-card" style={{ maxWidth: '420px', width: '100%' }}>
      <Card.Body className="p-4 p-md-5 text-center">
        <div className="icon-circle bg-danger bg-gradient mx-auto mb-3">
          <i className="bi bi-x-lg text-white"></i>
        </div>
        <h2 className="fw-bold">{t('verifyEmail.failedTitle')}</h2>
        <p className="text-muted mb-4">{t('verifyEmail.failedMessage')}</p>

        <Alert variant="danger">{error}</Alert>

        <Alert variant="info" className="text-start">
          <div className="d-flex">
            <i className="bi bi-info-circle-fill me-2 mt-1"></i>
            <div>
              {t('verifyEmail.failedInfo')}
            </div>
          </div>
        </Alert>

        <Button
          className="btn-gradient w-100 py-2 mt-3"
          size="lg"
          onClick={onSwitchToLogin}
        >
          {t('verifyEmail.goToLogin')}
        </Button>
      </Card.Body>
    </Card>
  );
};

export default VerifyEmail;
